import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, Save, Truck, Boxes } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

/** Dispatch drill-down for one sales voucher: GST breakdown, transport, and the batch-wise "what went to this client" editor. */
export default function DispatchBatchesModal({ d, onClose, onSaved }) {
  const [items, setItems] = useState(() => (d.items || []).map(it => ({ ...it, batches_dispatched: (it.batches_dispatched || []).map(b => ({ ...b })) })));
  const [transport, setTransport] = useState({ lr_no: d.lr_no || '', vehicle_no: d.vehicle_no || '', dispatch_through: d.dispatch_through || '' });
  const [saving, setSaving] = useState(false);

  const setBatch = (ii, bi, key, val) => setItems(list => list.map((it, i) => i !== ii ? it : {
    ...it, batches_dispatched: it.batches_dispatched.map((b, j) => j !== bi ? b : { ...b, [key]: val }) }));
  const addBatch = (ii) => setItems(list => list.map((it, i) => i !== ii ? it : { ...it, batches_dispatched: [...it.batches_dispatched, { batch_id: '', quantity: '' }] }));
  const delBatch = (ii, bi) => setItems(list => list.map((it, i) => i !== ii ? it : { ...it, batches_dispatched: it.batches_dispatched.filter((_, j) => j !== bi) }));
  const allocated = (it) => it.batches_dispatched.reduce((s, b) => s + (parseFloat(b.quantity) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...transport, items: items.map(it => ({ product_name: it.product_name,
        batches_dispatched: it.batches_dispatched.filter(b => b.batch_id && parseFloat(b.quantity) > 0).map(b => ({ batch_id: b.batch_id.trim(), quantity: parseFloat(b.quantity) })) })) };
      const r = await axios.patch(`${API}/sales/orders/${d.id}/dispatch-batches`, body, { withCredentials: true });
      if (r.data.warning) toast.warning(r.data.warning); else toast.success('Dispatch batches saved');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="dispatch-batches-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <div className="flex items-center gap-2 text-zinc-100 font-semibold"><Truck size={15} className="text-green-400" /> {d.invoice_number} <span className="text-zinc-500 font-normal text-sm">· {d.client}</span></div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Dispatch details · which batches went to this client</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="dispatch-modal-close"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs border-b border-zinc-800">
          <Stat label="Subtotal" value={fmt(d.subtotal || (d.total_amount - d.gst_amount))} />
          <Stat label={`GST${d.gst_rate ? ` ${d.gst_rate}%` : ''}`} value={d.gst_enabled ? fmt(d.gst_amount) : 'Non-GST'} tone={d.gst_enabled ? 'text-sky-300' : 'text-zinc-500'} testId="dispatch-gst" />
          <Stat label="Total" value={fmt(d.total_amount)} tone="text-emerald-300" />
          <Stat label="Payment" value={`${d.payment_status}${d.paid_amount ? ` · ${fmt(d.paid_amount)}` : ''}`} tone={d.payment_status === 'Paid' ? 'text-green-300' : 'text-amber-300'} />
        </div>

        <div className="px-5 py-3 grid sm:grid-cols-3 gap-2 border-b border-zinc-800">
          {[['dispatch_through', 'Transporter'], ['lr_no', 'LR no'], ['vehicle_no', 'Vehicle no']].map(([k, label]) => (
            <label key={k} className="text-[10px] uppercase tracking-wider text-zinc-500">{label}
              <input value={transport[k]} onChange={e => setTransport({ ...transport, [k]: e.target.value })} data-testid={`dispatch-${k}`}
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs text-zinc-100 normal-case tracking-normal focus:border-amber-500 outline-none" />
            </label>
          ))}
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-2 text-xs text-zinc-300 font-semibold"><Boxes size={13} className="text-amber-400" /> Batch-wise dispatch</div>
          {items.map((it, ii) => {
            const alloc = allocated(it);
            const diff = Math.round((it.quantity - alloc) * 1000) / 1000;
            return (
              <div key={ii} className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3" data-testid={`dispatch-item-${ii}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm text-zinc-100 font-medium">{it.product_name} <span className="text-zinc-500 text-xs font-mono">{it.quantity} {it.unit}</span></div>
                  <div className={`text-[10px] font-mono ${diff === 0 ? 'text-emerald-400' : diff > 0 ? 'text-amber-300' : 'text-red-300'}`} data-testid={`dispatch-item-${ii}-alloc`}>
                    allocated {alloc} / {it.quantity} {it.unit}{diff !== 0 && ` (${diff > 0 ? `${diff} unassigned` : `${-diff} over`})`}
                  </div>
                </div>
                <div className="mt-2 space-y-1.5">
                  {it.batches_dispatched.map((b, bi) => (
                    <div key={bi} className="flex items-center gap-2">
                      <input value={b.batch_id || ''} onChange={e => setBatch(ii, bi, 'batch_id', e.target.value)} placeholder="Batch no e.g. F261009"
                        data-testid={`dispatch-item-${ii}-batch-${bi}`}
                        className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono text-sky-200 focus:border-amber-500 outline-none" />
                      <input type="number" step="any" min="0" value={b.quantity ?? ''} onChange={e => setBatch(ii, bi, 'quantity', e.target.value)} placeholder="kg"
                        data-testid={`dispatch-item-${ii}-qty-${bi}`}
                        className="w-24 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono text-zinc-100 focus:border-amber-500 outline-none" />
                      <span className="text-[10px] text-zinc-500">{it.unit}</span>
                      <button onClick={() => delBatch(ii, bi)} className="text-zinc-500 hover:text-red-300" data-testid={`dispatch-item-${ii}-del-${bi}`}><Trash2 size={13} /></button>
                    </div>
                  ))}
                  <button onClick={() => addBatch(ii)} data-testid={`dispatch-item-${ii}-add`}
                    className="text-[11px] flex items-center gap-1 text-amber-300 hover:text-amber-200"><Plus size={11} /> Add batch</button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="dispatch-batches-save"
            className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 disabled:opacity-50 flex items-center gap-1"><Save size={12} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone = 'text-zinc-100', testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`font-mono text-xs mt-0.5 ${tone}`}>{value}</div>
  </div>
);
